import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Space } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { getProducts, createProduct, updateProduct, deleteProduct, createProductDirectory } from '../utils/api';

interface Product {
  id: number;
  productid: string | null;
  s3path: string | null;
  name: string;
  description: string;
}

const ProductManagementPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { token } = useAuth();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await getProducts(token);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.results || data);
      } else {
        message.error('Failed to fetch products.');
      }
    } catch (error) {
      message.error('An error occurred while fetching products.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    form.setFieldsValue(product);
    setIsModalVisible(true);
  };

  const handleDelete = async (productId: number) => {
    if (!token) return;
    try {
      const response = await deleteProduct(productId, token);
      if (response.ok) {
        message.success('Product deleted successfully.');
        fetchProducts();
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Failed to delete product.');
      }
    } catch (error) {
      message.error('An error occurred while deleting the product.');
    }
  };

  const handleOk = async () => {
    if (!token) return;
    try {
      const values = await form.validateFields();
      let response;
      if (editingProduct) {
        response = await updateProduct(editingProduct.id, values, token);
      } else {
        response = await createProduct(values, token);
      }

      if (response.ok) {
        message.success(`Product ${editingProduct ? 'updated' : 'created'} successfully.`);
        setIsModalVisible(false);
        fetchProducts();
      } else {
        const errorData = await response.json();
        const errorMessage = Object.entries(errorData).map(([key, value]) => `${key}: ${value}`).join('; ');
        message.error(errorMessage || `Failed to ${editingProduct ? 'update' : 'create'} product.`);
      }
    } catch (error) {
      // Form validation can throw an error
      console.error('Validation Failed:', error);
    }
  };

  const handleCreateDirectory = async (productId: number) => {
    if (!token) return;
    try {
      const response = await createProductDirectory(productId, token);
      const data = await response.json();
      if (response.ok) {
        message.success(data.message);
      } else {
        message.warning(data.message || data.error || 'Failed to create directory.');
      }
    } catch (error) {
      message.error('An error occurred while creating the directory.');
    }
  };

  const columns = [
    { title: 'Product ID', dataIndex: 'productid', key: 'productid' },
    { title: 'S3 Path', dataIndex: 's3path', key: 's3path' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Product) => (
        <Space size="middle">
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          <Button type="dashed" onClick={() => handleCreateDirectory(record.id)}>Create Directory</Button>
          <Popconfirm
            title="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Product Management</h1>
        <Button type="primary" onClick={handleAdd}>
          Add Product
        </Button>
      </div>
      <Table columns={columns} dataSource={products} rowKey="id" loading={loading} />

      <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        okText={editingProduct ? 'Save' : 'Create'}
      >
        <Form form={form} layout="vertical" name="product_form">
          <Form.Item
            name="productid"
            label="Product ID"
            rules={[{ required: true, message: 'Please input the product ID!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="s3path"
            label="S3 Path"
            rules={[{ required: true, message: 'Please input the S3 path!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: 'Please input the product name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductManagementPage; 